import { ENV_CROPS_PRICE_UOM_LABELS } from 'appConstants/envCropsOrder';
import type { Selector } from 'reselect';
import { createSelector } from 'reselect';

import type {
  IEnvCropsProduct,
  IEnvCropsProductsState,
  IIndexedEnvCropsProducts,
  IIndexedTreatments,
  ITreatment,
} from './types';

export const selectProducts = (state: IEnvCropsProductsState) =>
  state.products.map((product) => ({
    ...product,
    packages: product.packages.map((pkg) => ({
      ...pkg,
      uomPriceMemonic: pkg.uomPriceMemonic
        ? ENV_CROPS_PRICE_UOM_LABELS[pkg.uomPriceMemonic] || pkg.uomPriceMemonic
        : pkg.uomPriceMemonic,
    })),
  }));

export const selectIndexedProducts: Selector<IEnvCropsProductsState, IIndexedEnvCropsProducts> =
  createSelector(selectProducts, (products) =>
    products.reduce((indexed: { [x: string]: any }, product: IEnvCropsProduct) => {
      if (product.itemReference) {
        indexed[product.itemReference] = product;
      }
      return indexed;
    }, {}),
  );

export const selectProduct: Selector<IEnvCropsProductsState, IEnvCropsProduct> = createSelector(
  [
    selectIndexedProducts,
    (_: IEnvCropsProductsState, packagedItemReference: string) => packagedItemReference,
  ],
  (indexedProducts, packagedItemReference) => indexedProducts[packagedItemReference],
);

export const selectIndexedTreatments: Selector<IEnvCropsProductsState, IIndexedTreatments> =
  createSelector(selectProducts, (products) =>
    products.reduce((indexed: { [x: string]: any }, product: IEnvCropsProduct) => {
      if (product.treatments && Array.isArray(product.treatments)) {
        for (const treatment of product.treatments) {
          indexed[treatment.treatmentReference] = treatment;
        }
      }
      return indexed;
    }, {}),
  );

export const selectTreatment: Selector<IEnvCropsProductsState, ITreatment> = createSelector(
  [
    selectIndexedTreatments,
    (_: IEnvCropsProductsState, treatmentReference: string) => treatmentReference,
  ],
  (indexedTreatments, treatmentReference) => indexedTreatments[treatmentReference],
);
